import type { DynamicGridItem } from '@cast/design-system';

import {
  WORKLOAD_CATEGORIES,
  WORKLOAD_CATEGORY_FILTER_OPTIONS,
} from '../../_constants';
import type { WorkloadCategory } from '../../_constants';

export enum WorkloadDashboardItemId {
  CpuCost = 'workload-cpu-cost',
  MemoryCost = 'workload-memory-cost',
  GpuCost = 'workload-gpu-cost',
  StorageCost = 'workload-storage-cost',
  ComputeSpend = 'workload-compute-spend',
  CpuAutoscaling = 'workload-cpu-autoscaling',
  MemoryAutoscaling = 'workload-memory-autoscaling',
  OomKills = 'workload-oom-kills',
  CpuPrediction = 'workload-cpu-prediction',
  JvmHeap = 'workload-jvm-heap',
  HpaCharts = 'workload-hpa-charts',
  GpuUtilization = 'workload-gpu-utilization',
  GpuUtilizationBreakdown = 'workload-gpu-utilization-breakdown',
  GpuMemory = 'workload-gpu-memory',
  GpuMemoryWaste = 'workload-gpu-memory-waste',
  GpuCount = 'workload-gpu-count',
  GpuWaste = 'workload-gpu-waste',
  ReliabilityLatency = 'workload-reliability-latency',
  ReliabilityTraffic = 'workload-reliability-traffic',
  NetworkCost = 'workload-network-cost',
  NetworkTraffic = 'workload-network-traffic',
  NetworkTrafficBreakdown = 'workload-network-traffic-breakdown',
  CrossAzCommunicationCosts = 'workload-cross-az-communication-costs',
  CrossAzCommunicationTraffic = 'workload-cross-az-communication-traffic',
}

export type WorkloadDashboardItem = DynamicGridItem & {
  id: WorkloadDashboardItemId;
};

export const WORKLOAD_DASHBOARD_ITEMS: WorkloadDashboardItem[] = [
  {
    id: WorkloadDashboardItemId.CpuCost,
    title: 'CPU cost',
    description: 'Cost per requested vCPU over time',
    x: 0,
    y: 0,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.MemoryCost,
    title: 'Memory cost',
    description: 'Cost per requested GB over time',
    x: 0,
    y: 8,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuCost,
    title: 'GPU cost',
    description: 'Cost per requested GPU over time',
    x: 0,
    y: 12,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.StorageCost,
    title: 'Storage cost',
    description: 'Cost per requested GB over time',
    x: 0,
    y: 16,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.ComputeSpend,
    title: 'Compute spend',
    description: 'Compute spend breakdown over time',
    x: 0,
    y: 20,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.CpuAutoscaling,
    title: 'CPU autoscaling',
    description: 'CPU usage and right-sizing recommendations',
    x: 0,
    y: 28,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.MemoryAutoscaling,
    title: 'Memory autoscaling',
    description: 'Memory usage and right-sizing recommendations',
    x: 0,
    y: 32,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.OomKills,
    title: 'OOM kills',
    description: 'OOM kill events over time',
    x: 0,
    y: 36,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.CpuPrediction,
    title: 'Predictive autoscaling: CPU',
    description: 'CPU usage with predictive scaling recommendations',
    x: 0,
    y: 40,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.JvmHeap,
    title: 'JVM heap',
    description: 'JVM heap and non-heap memory usage',
    x: 0,
    y: 44,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.HpaCharts,
    title: 'Horizontal autoscaling',
    description: 'Replica count and scaling events over time',
    x: 0,
    y: 49,
    w: 12,
    h: 5,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuUtilization,
    title: 'GPU utilization',
    description: 'GPU utilization over time',
    x: 0,
    y: 54,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuUtilizationBreakdown,
    title: 'GPU utilization',
    description: 'Average GPU utilization and GPU memory utilization over time',
    x: 0,
    y: 58,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuMemory,
    title: 'GPU memory',
    description: 'GPU memory usage over time',
    x: 0,
    y: 62,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuMemoryWaste,
    title: 'GPU Memory waste',
    description: 'GPU memory waste over time',
    x: 0,
    y: 66,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuCount,
    title: 'GPU count',
    description: 'GPU instance count over time',
    x: 0,
    y: 70,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.GpuWaste,
    title: 'GPU waste',
    description: 'GPU waste over time',
    x: 0,
    y: 74,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.ReliabilityLatency,
    title: 'Reliability latency',
    description: 'Request latency over time',
    x: 0,
    y: 78,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.ReliabilityTraffic,
    title: 'Reliability traffic',
    description: 'Request traffic over time',
    x: 0,
    y: 82,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.NetworkCost,
    title: 'Network cost',
    description: 'Network cost breakdown over time',
    x: 0,
    y: 86,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.NetworkTraffic,
    title: 'Network traffic',
    description: 'Network traffic over time',
    x: 0,
    y: 90,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.NetworkTrafficBreakdown,
    title: 'Traffic breakdown',
    description: 'Network traffic breakdown over time',
    x: 0,
    y: 94,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.CrossAzCommunicationCosts,
    title: 'Cross-AZ communication costs',
    description: 'Cross-AZ communication costs and highest to lowest list',
    x: 0,
    y: 98,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadDashboardItemId.CrossAzCommunicationTraffic,
    title: 'Cross-AZ communication traffic',
    description: 'Cross-AZ communication traffic and highest to lowest list',
    x: 0,
    y: 102,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
];

export const WORKLOAD_DASHBOARD_DEFAULT_VISIBLE: Record<
  Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.SAVINGS>,
  WorkloadDashboardItemId[]
> = {
  // Default view charts per Phase-1 handoff (autoscaling + reliability + spend).
  [WORKLOAD_CATEGORIES.DEFAULT]: [
    WorkloadDashboardItemId.CpuAutoscaling,
    WorkloadDashboardItemId.MemoryAutoscaling,
    WorkloadDashboardItemId.HpaCharts,
    WorkloadDashboardItemId.ReliabilityTraffic,
    WorkloadDashboardItemId.ReliabilityLatency,
    WorkloadDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.AUTOSCALING]: [
    WorkloadDashboardItemId.CpuAutoscaling,
    WorkloadDashboardItemId.MemoryAutoscaling,
    WorkloadDashboardItemId.CpuPrediction,
    WorkloadDashboardItemId.HpaCharts,
    WorkloadDashboardItemId.JvmHeap,
  ],
  [WORKLOAD_CATEGORIES.COMPUTE]: [
    WorkloadDashboardItemId.CpuCost,
    WorkloadDashboardItemId.MemoryCost,
    WorkloadDashboardItemId.GpuCost,
    WorkloadDashboardItemId.StorageCost,
    WorkloadDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.RELIABILITY]: [
    WorkloadDashboardItemId.ReliabilityLatency,
    WorkloadDashboardItemId.ReliabilityTraffic,
  ],
  [WORKLOAD_CATEGORIES.NETWORK]: [
    WorkloadDashboardItemId.NetworkCost,
    WorkloadDashboardItemId.NetworkTraffic,
    WorkloadDashboardItemId.NetworkTrafficBreakdown,
    WorkloadDashboardItemId.CrossAzCommunicationCosts,
    WorkloadDashboardItemId.CrossAzCommunicationTraffic,
  ],
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: [
    WorkloadDashboardItemId.GpuUtilizationBreakdown,
    WorkloadDashboardItemId.GpuMemoryWaste,
    WorkloadDashboardItemId.GpuWaste,
  ],
};

/** Primary category per card (folder ownership) for customize-drawer grouping/filter. */
export const WORKLOAD_DASHBOARD_ITEM_CATEGORY: Record<
  WorkloadDashboardItemId,
  Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.DEFAULT>
> = {
  [WorkloadDashboardItemId.CpuCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.MemoryCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.GpuCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.StorageCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.ComputeSpend]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.CpuAutoscaling]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.MemoryAutoscaling]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.OomKills]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.CpuPrediction]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.JvmHeap]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.HpaCharts]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.GpuUtilization]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuUtilizationBreakdown]:
    WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuMemory]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuMemoryWaste]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuCount]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuWaste]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.ReliabilityLatency]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadDashboardItemId.ReliabilityTraffic]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadDashboardItemId.NetworkCost]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.NetworkTraffic]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.NetworkTrafficBreakdown]:
    WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.CrossAzCommunicationCosts]:
    WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.CrossAzCommunicationTraffic]:
    WORKLOAD_CATEGORIES.NETWORK,
};

export const WORKLOAD_DRAWER_CATEGORY_OPTIONS =
  WORKLOAD_CATEGORY_FILTER_OPTIONS.filter(
    ({ value }) => value !== WORKLOAD_CATEGORIES.SAVINGS
  );

export const getWorkloadDashboardStorageKey = (category: string) =>
  `workload-dashboard-${category}-v10`;
